import React, { Suspense, useCallback } from "react";
import { graphql } from "react-relay";

import { faArrowToBottom } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useArchitecture } from "~/lib/hooks";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import {
	desktopDownloadLink,
	isMacos,
	macOsArmDesktopDownloadUri,
	macOsX64DesktopDownloadUri,
	reduceEdgesToNodes,
} from "~/lib/utils";
import { Button } from "~/ui";
import type { ButtonProps } from "~/ui/button";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { ReactComponent as ArrowToDownloadsNoNumber } from "./images/arrow-no-number.svg";
import { ReactComponent as FellowAppImageMac } from "./images/fellow-app-image-mac.svg";
import { ReactComponent as FellowAppImageWindows } from "./images/fellow-app-image-windows.svg";
import { JourneyContext } from "./JourneyContext";

import type { DesktopPageUsersQuery } from "~/__generated__/DesktopPageUsersQuery.graphql";

import styles from "./css/DesktopPage.module.css";

export const DESKTOP_PATH = "/o/desktop/";

function DownloadButton({
	text,
	url,
	setShowDownloadArrow,
	...rest
}: { text: string; url: string; setShowDownloadArrow: (val: boolean) => void } & ButtonProps) {
	const { nextStep } = React.useContext(JourneyContext);
	const [loading, setLoading] = React.useState(false);

	const variant = rest.variant || "primary";

	const onNextStep = useCallback(() => {
		dispatchAnalyticsTrack("Download Desktop App Clicked", { feature: "Onboarding" });
		setTimeout(nextStep, 1500);
	}, [nextStep]);

	const onDownloadClick = useCallback(() => {
		onNextStep();
		setShowDownloadArrow(true);
		setLoading(true);
	}, [setShowDownloadArrow, onNextStep]);

	return loading ? (
		<LoadingSpinner inline dimmer={false} />
	) : (
		<a href={loading ? "" : url} download>
			<Button {...rest} variant={variant} className={styles.downloadButton} onClick={onDownloadClick}>
				{text}
				<FontAwesomeIcon icon={faArrowToBottom} className={styles.downloadIcon} />
			</Button>
		</a>
	);
}

function Subitle() {
	const { viewer, users } = useLazyLoadQuery<DesktopPageUsersQuery>(
		graphql`
			query DesktopPageUsersQuery {
				viewer {
					user {
						id
					}
				}
				users {
					edges {
						node {
							id
							firstName
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-or-network" },
	);

	// A list of users who are already using the app (for social proof)
	const subtitleText = React.useMemo(() => {
		const userFirstNames = reduceEdgesToNodes(users?.edges)
			.filter(({ id }) => id !== viewer.user.id)
			.map(node => node.firstName);

		const endText = (
			<>
				already using Fellow to <b>prepare</b>, <b>record</b>, and <b>collaborate</b> on their meetings.
			</>
		);

		let namesToShow;
		switch (userFirstNames.length) {
			case 0:
				return (
					<>
						The only way to <b>prepare</b>, <b>record</b>, and <b>collaborate</b>
						&nbsp;on your meetings.
					</>
				);
			case 1:
				return (
					<span>
						<b>{userFirstNames[0]}</b> is {endText}
					</span>
				);
			case 2:
				return (
					<span>
						<b>{userFirstNames[0]}</b> and <b>{userFirstNames[1]}</b> are {endText}
					</span>
				);
			case 3:
				return (
					<span>
						<b>{userFirstNames[0]}</b>, <b>{userFirstNames[1]}</b> and <b>{userFirstNames[2]}</b> are {endText}
					</span>
				);
			default:
				namesToShow = userFirstNames.slice(0, 2);
				return (
					<span>
						<b>{namesToShow[0]}</b>, <b>{namesToShow[1]}</b> and <b>{userFirstNames.length - 2} others</b> are {endText}
					</span>
				);
		}
	}, [viewer.user, users]);

	return <div className={styles.subtitle}>{subtitleText}</div>;
}

export default function DesktopPage() {
	const [showDownloadArrow, setShowDownloadArrow] = React.useState(false);
	const architecture = useArchitecture();

	const downloadSection = React.useMemo(() => {
		if (!isMacos)
			return (
				<DownloadButton text="Download Fellow" url={desktopDownloadLink} setShowDownloadArrow={setShowDownloadArrow} />
			);
		if (architecture == "arm")
			return (
				<DownloadButton
					text="Download Fellow"
					url={macOsArmDesktopDownloadUri}
					setShowDownloadArrow={setShowDownloadArrow}
				/>
			);
		if (architecture == "x64")
			return (
				<DownloadButton
					text="Download Fellow"
					url={macOsX64DesktopDownloadUri}
					setShowDownloadArrow={setShowDownloadArrow}
				/>
			);
		return (
			<div className={styles.multiButtonContainer}>
				<DownloadButton text="Intel Mac" url={macOsX64DesktopDownloadUri} setShowDownloadArrow={setShowDownloadArrow} />
				<DownloadButton
					variant="secondary"
					text="Apple Silicon"
					url={macOsArmDesktopDownloadUri}
					setShowDownloadArrow={setShowDownloadArrow}
				/>
			</div>
		);
	}, [architecture]);

	return architecture ? (
		<>
			<div className={styles.title}>Download and Install Fellow</div>
			<Suspense
				fallback={
					<>
						The only way to <b>prepare</b>, <b>record</b>, and <b>collaborate</b>
						&nbsp;on your meetings.
					</>
				}
			>
				<Subitle />
			</Suspense>
			{isMacos ? (
				<FellowAppImageMac className={styles.fellowAppImage} />
			) : (
				<FellowAppImageWindows className={styles.fellowAppImage} />
			)}
			{downloadSection}
			{showDownloadArrow && (
				<div className={styles.arrowToDownloads}>
					<ArrowToDownloadsNoNumber />
				</div>
			)}
		</>
	) : (
		<></>
	);
}
